import { EuroCircleOutlined, PercentageOutlined } from "@ant-design/icons"
import { Col, Row, Spin, Typography, Descriptions, Divider } from "antd"
import MissingDocList from "./MissingDocList"

const { Text, Title } = Typography

const blockstyle = {
  background: "white",
  borderRadius: 15,
  padding: 25,
  width: "100%",
  height: "100%",
}

export default function Overview({
  yearInvoices,
  monthInvoices,
  prevYearInvoices,
  prevMonthInvoices,
  loading: invoicesLoading,
  selectedYear,
  selectedMonth,
  months,
  warehouseOrders,
  warehouseOrdersLoading,
  forSales = false,
  salesReportData,
  salesReportLoading,
}) {
  // Compute totals
  const yearTotal = yearInvoices?.reduce((a, i) => a + (i.totalData?.totalNetoSum || 0), 0) || 0
  const prevYearTotal =
    prevYearInvoices?.reduce((a, i) => a + (i.totalData?.totalNetoSum || 0), 0) || 0
  const monthTotal = monthInvoices?.reduce((a, i) => a + (i.totalData?.totalNetoSum || 0), 0) || 0
  const prevMonthTotal =
    prevMonthInvoices?.reduce((a, i) => a + (i.totalData?.totalNetoSum || 0), 0) || 0

  // Warehouse stats
  const warehouseOrderCount = warehouseOrders?.length || 0
  const warehouseOrdersTotal =
    warehouseOrders?.reduce((sum, o) => {
      const price = Number(o.price) || 0
      const amount = Number(o.amountCreated / 1000) || 0
      return sum + price * amount
    }, 0) || 0

  // Month label
  let selectedMonthLabel = ""
  if (selectedMonth !== "wholeYear" && months) {
    const m = months.find((x) => x.key === selectedMonth)
    selectedMonthLabel = m?.label || ""
  }
  const performancePeriodLabel =
    selectedMonth !== "wholeYear" ? `${selectedMonthLabel} ${selectedYear}` : `Year ${selectedYear}`

  // Currency formatter
  const formatCurrency = (value) =>
    `€${Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`

  return (
    <div>
      <Row gutter={[15, 15]}>
        {/* Year Totals */}
        {!forSales && (
          <Col xs={24} sm={12} md={12} lg={6}>
            <div style={blockstyle}>
              <Row justify="space-between" align="middle">
                <Text>Totals for {selectedYear}</Text>
                <EuroCircleOutlined />
              </Row>
              <Title level={3}>{invoicesLoading ? <Spin /> : formatCurrency(yearTotal)}</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Previous year ({selectedYear - 1}):{" "}
                {invoicesLoading ? <Spin size="small" /> : formatCurrency(prevYearTotal)}
              </Text>
            </div>
          </Col>
        )}

        {/* Month Totals */}
        <Col xs={24} sm={12} md={12} lg={forSales ? 8 : 6}>
          <div style={blockstyle}>
            <Row justify="space-between" align="middle">
              <Text>
                Totals for{" "}
                {selectedMonth !== "wholeYear"
                  ? `${selectedMonthLabel} ${selectedYear}`
                  : selectedYear}
              </Text>
              <EuroCircleOutlined />
            </Row>
            <Title level={3}>{invoicesLoading ? <Spin /> : formatCurrency(monthTotal)}</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Previous year ({selectedYear - 1}
              {selectedMonth !== "wholeYear" ? `, ${selectedMonthLabel}` : ""}):{" "}
              {invoicesLoading ? <Spin size="small" /> : formatCurrency(prevMonthTotal)}
            </Text>
          </div>
        </Col>

        {/* Sales Performance */}
        {forSales && (
          <Col xs={24} sm={12} md={12} lg={12}>
            <div style={blockstyle}>
              <Row justify="space-between" align="middle">
                <Text>Sales Performance ({performancePeriodLabel})</Text>
                <PercentageOutlined />
              </Row>

              <Row style={{ marginTop: 10 }} align="top">
                {/* Left metrics */}
                <Col span={11}>
                  <Descriptions size="small" column={1} bordered={false}>
                    <Descriptions.Item label="Profit (5% of neto)">
                      {salesReportLoading ? (
                        <Spin size="small" />
                      ) : (
                        formatCurrency(salesReportData?.profit)
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label="30 Day payment missing (-5%)"
                      contentStyle={{
                        color:
                          (salesReportData?.missingPaymentDeduction || 0) > 0
                            ? "#ff4d4f"
                            : undefined,
                      }}>
                      –{" "}
                      <MissingDocList
                        linkText={formatCurrency(salesReportData?.missingPaymentDeduction)}
                        collectionName="invoices"
                        ids={salesReportData?.missingPaymentInvoiceIds || []}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item
                      label="Client Bankruptcy (-10%)"
                      contentStyle={{
                        color:
                          (salesReportData?.bankruptcyRiskDeduction || 0) > 0
                            ? "#ff4d4f"
                            : undefined,
                      }}>
                      –{" "}
                      {salesReportLoading ? (
                        <Spin size="small" />
                      ) : (
                        formatCurrency(salesReportData?.bankruptcyRiskDeduction)
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>

                {/* Divider */}
                <Divider type="vertical" style={{ height: "100%", margin: "0 12px" }} />

                {/* Right stuck-order sums */}
                <Col span={11}>
                  <Descriptions size="small" column={1} bordered={false}>
                    <Descriptions.Item label="Stuck in warehouse (past 60 days)">
                      <MissingDocList
                        linkText={formatCurrency(salesReportData?.stuck60Base)}
                        collectionName="orders"
                        ids={salesReportData?.stuck60OrderIds || []}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Stuck in warehouse (past 30 days)">
                      <MissingDocList
                        linkText={formatCurrency(salesReportData?.stuck30Base)}
                        collectionName="orders"
                        ids={salesReportData?.stuck30OrderIds || []}
                      />
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>

              {/* Total Earnings */}
              <Divider style={{ margin: "16px 0" }} />
              <Row justify="start" align="middle">
                <Col span={24}>
                  <Title level={5} style={{ margin: 0, textAlign: "start" }}>
                    Total Earnings This Month
                  </Title>
                </Col>
                <Col span={24}>
                  <Text strong style={{ fontSize: "1.5em", display: "block", textAlign: "start" }}>
                    {salesReportLoading ? (
                      <Spin />
                    ) : (
                      formatCurrency(salesReportData?.calculatedEarnings)
                    )}
                  </Text>
                </Col>
              </Row>
            </div>
          </Col>
        )}

        {/* Warehouse Orders */}
        <Col span={4}>
          <div style={blockstyle}>
            <Row justify="space-between" align="middle">
              <Text>In Warehouse</Text>
              <EuroCircleOutlined />
            </Row>
            <Title level={2}>
              {warehouseOrdersLoading ? <Spin /> : formatCurrency(warehouseOrdersTotal)}
            </Title>
            <Text style={{ color: warehouseOrderCount >= 5 ? "#ff7875" : "lightgray" }}>
              Orders in warehouse:{" "}
              {warehouseOrdersLoading ? <Spin size="small" /> : warehouseOrderCount}
            </Text>
          </div>
        </Col>
      </Row>
    </div>
  )
}
